import React, { useState } from 'react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';

const ContactContainer = styled.section`
  padding: 80px 2rem;
  background: ${props => props.theme.colors.backgroundLight};
`;

const ContactContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const InfoSection = styled.div``;

const FormSection = styled.div``;

const SectionTitle = styled.h2`
  font-size: 2.8rem;
  color: ${props => props.theme.colors.textWarm};
  margin-bottom: 2rem;
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Description = styled.p`
  font-size: 1.1rem;
  line-height: 1.9;
  color: ${props => props.theme.colors.text};
  margin-bottom: 3rem;
  font-weight: 300;
`;

const ContactInfo = styled.div`
  margin-bottom: 3rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: ${props => props.theme.colors.background};
  border-radius: 10px;
  border: 1px solid ${props => props.theme.colors.borderLight};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const ContactIcon = styled.div`
  width: 50px;
  height: 50px;
  background: ${props => props.theme.colors.primary};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-left: 1rem;
  color: white;
`;

const ContactDetails = styled.div`
  flex: 1;
`;

const ContactLabel = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textLight};
  margin-bottom: 0.25rem;
`;

const ContactValue = styled.div`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.textWarm};
  font-weight: 500;
`;

const Form = styled.form`
  background: ${props => props.theme.colors.background};
  padding: 3rem;
  border-radius: 25px;
  box-shadow: ${props => props.theme.shadows.warm};
  border: 3px solid ${props => props.theme.colors.borderLight};
`;

const FormTitle = styled.h3`
  font-size: 2rem;
  color: ${props => props.theme.colors.textWarm};
  margin-bottom: 1.5rem;
  text-align: center;
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 500;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 8px;
  transition: background 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.secondary};
  }

  &:disabled {
    background: ${props => props.theme.colors.textLight};
    cursor: not-allowed;
  }
`;

const PrivacyNote = styled.p`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textLight};
  text-align: center;
  margin-top: 1rem;
  line-height: 1.4;
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' }>`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
  font-weight: 500;
  background: ${props => props.type === 'success' ? '#d4edda' : '#f8d7da'};
  color: ${props => props.type === 'success' ? '#155724' : '#721c24'};
  border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : '#f5c6cb'};
`;

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatusMessage(null);

    try {
      // EmailJS configuration
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID';
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID';
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';

      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || 'לא צוין',
        message: formData.message
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      setStatusMessage({
        type: 'success',
        text: 'תודה על פנייתך! אחזור אליך בהקדם האפשרי.'
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('EmailJS error:', error);
      setStatusMessage({
        type: 'error',
        text: 'אופס, משהו השתבש. אנא נסה שוב או צור קשר ישירות במייל או בטלפון.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactContainer id="contact" aria-labelledby="contact-title">
      <ContactContent>
        <InfoSection>
          <SectionTitle id="contact-title">צור קשר</SectionTitle>
          <Description>
            אני יודעת שזה לא תמיד קל לפנות לעזרה, וזה בסדר גמור. אני כאן בשבילכם - לשמוע,
            לענות על שאלות, ופשוט להיות נוכחת. אפשר להתקשר, לכתוב, או למלא את הטופס.
            אין לחץ, אין שיפוטיות - רק מקום בטוח להתחיל.
          </Description>

          <ContactInfo role="list" aria-label="דרכי יצירת קשר">
            <ContactItem role="listitem">
              <ContactIcon aria-label="טלפון">☎</ContactIcon>
              <ContactDetails>
                <ContactLabel>בואו נדבר</ContactLabel>
                <ContactValue><a href="tel:0545954754" aria-label="התקשר למספר 054-5954754">054-5954754</a></ContactValue>
              </ContactDetails>
            </ContactItem>

            <ContactItem role="listitem">
              <ContactIcon aria-label="דואר אלקטרוני">✉</ContactIcon>
              <ContactDetails>
                <ContactLabel>כתבו לי</ContactLabel>
                <ContactValue><a href="mailto:leefreund90@gmail.com" aria-label="שלח דואר אלקטרוני לכתובת leefreund90@gmail.com">leefreund90@gmail.com</a></ContactValue>
              </ContactDetails>
            </ContactItem>

            <ContactItem role="listitem">
              <ContactIcon aria-label="מיקום">📍</ContactIcon>
              <ContactDetails>
                <ContactLabel>איפה אני נמצאת</ContactLabel>
                <ContactValue>שוהם</ContactValue>
              </ContactDetails>
            </ContactItem>
          </ContactInfo>
        </InfoSection>
        
        <FormSection>
          <Form onSubmit={handleSubmit} aria-labelledby="form-title">
            <FormTitle id="form-title">השאירו פרטים</FormTitle>

            {statusMessage && (
              <StatusMessage
                type={statusMessage.type}
                role="alert"
                aria-live="polite"
              >
                {statusMessage.text}
              </StatusMessage>
            )}

            <FormGroup>
              <Label htmlFor="name">שם מלא *</Label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                aria-required="true"
                aria-label="הכנס שם מלא"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">דוא"ל *</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                aria-required="true"
                aria-label="הכנס כתובת דואר אלקטרוני"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="phone">טלפון</Label>
              <Input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                aria-label="הכנס מספר טלפון (אופציונלי)"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="message">הודעה *</Label>
              <TextArea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="ספר/י בקצרה על מה שמעסיק אותך ואיך אוכל לעזור..."
                required
                aria-required="true"
                aria-label="הכנס הודעה"
              />
            </FormGroup>

            <SubmitButton
              type="submit"
              disabled={isSubmitting}
              aria-label={isSubmitting ? 'שולח טופס' : 'שלח טופס צור קשר'}
            >
              {isSubmitting ? 'שולח...' : 'השאירו פרטים'}
            </SubmitButton>

            <PrivacyNote>
              הפרטים שלך יישמרו בסודיות מלאה ויהיו מוגנים בהתאם לחוק הגנת הפרטיות.
              לא נעשה שימוש בפרטיך למטרות שיווק.
            </PrivacyNote>
          </Form>
        </FormSection>
      </ContactContent>
    </ContactContainer>
  );
};

export default Contact;